import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { stripAssetUpdateEditorFormData } from '../api/assetPayloadNormalize';
import { ISRAEL_COUNTRY_ID } from './assetCountryCurrency';
import { IMPLICIT_ASSET_MIN_CAPACITY } from './assetCreateCapacity';

/** United States — region/state picker stays on asset create. */
export const US_COUNTRY_ID = 2;

export const ASSET_CREATE_DEFAULT_MAX_CAPACITY = 1000;

const ISRAEL_DEFAULT_REGION_LABEL_PATTERNS = [
  /^center$/i,
  /^central$/i,
  /^מרכז$/i,
  /מחוז המרכז/,
  /central district/i,
] as const;

function coerceNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function isIsraelCountryId(countryId: unknown): boolean {
  return coerceNumericId(countryId) === ISRAEL_COUNTRY_ID;
}

export function isUnitedStatesCountryId(countryId: unknown): boolean {
  return coerceNumericId(countryId) === US_COUNTRY_ID;
}

/** Asset create/edit shows district/state only for the United States. */
export function assetCreateShowsRegionField(countryId: unknown): boolean {
  return isUnitedStatesCountryId(countryId);
}

/** @deprecated Alias for {@link assetCreateShowsRegionField}. */
export const assetMutationShowsRegionField = assetCreateShowsRegionField;

/**
 * Israel assets default to the Central ("center") district when the API requires `state_id`.
 */
export function resolveIsraelDefaultRegionId(
  regions: readonly GeneralInfoPicklistItem[],
): number | null {
  for (const region of regions) {
    const label = region.label?.trim() ?? '';
    if (!label) {
      continue;
    }
    if (ISRAEL_DEFAULT_REGION_LABEL_PATTERNS.some((pattern) => pattern.test(label))) {
      const id = Number(region.id);
      return Number.isFinite(id) ? id : null;
    }
  }
  return null;
}

/** Strip capacity and non-US region fields hidden from create/edit asset forms. */
export function assetMutationVisibleFormData(
  body: Record<string, unknown>,
  countryId: unknown = body.country_id,
): Record<string, unknown> {
  const next = { ...body };
  delete next.min_capacity;
  delete next.max_capacity;
  if (!assetCreateShowsRegionField(countryId)) {
    delete next.state_id;
  }
  return next;
}

/** @deprecated Alias for {@link assetMutationVisibleFormData}. */
export const assetCreateVisibleFormData = assetMutationVisibleFormData;

/** Update editor: strip PATCH-omitted roots, then hide capacity and Israel region. */
export function assetEditVisibleFormData(body: Record<string, unknown>): Record<string, unknown> {
  const countryId = body.country_id;
  const next = assetMutationVisibleFormData(stripAssetUpdateEditorFormData(body), countryId);
  if (assetCreateShowsRegionField(countryId)) {
    next.state_id = body.state_id ?? null;
  }
  return next;
}

export type AssetMutationSubmitDefaultsMode = 'create' | 'update';

/** Wire defaults applied on create/update submit when fields are omitted from the UI. */
export function applyAssetMutationSubmitDefaults(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[],
  mode: AssetMutationSubmitDefaultsMode = 'create',
): Record<string, unknown> {
  const next = { ...body };
  const countryId = coerceNumericId(next.country_id);

  if (countryId === ISRAEL_COUNTRY_ID) {
    const currentRegion = coerceNumericId(next.state_id);
    if (currentRegion === null) {
      const defaultRegionId = resolveIsraelDefaultRegionId(regions);
      if (defaultRegionId !== null) {
        next.state_id = defaultRegionId;
      }
    }
  }

  if (mode === 'create') {
    next.min_capacity = IMPLICIT_ASSET_MIN_CAPACITY;
    next.max_capacity = ASSET_CREATE_DEFAULT_MAX_CAPACITY;
    if (typeof next.asset_status !== 'string' || !next.asset_status.trim()) {
      next.asset_status = 'active';
    }
  } else {
    if (coerceNumericId(next.min_capacity) === null) {
      next.min_capacity = IMPLICIT_ASSET_MIN_CAPACITY;
    }
    if (coerceNumericId(next.max_capacity) === null) {
      next.max_capacity = ASSET_CREATE_DEFAULT_MAX_CAPACITY;
    }
  }
  return next;
}

/** @deprecated Alias for {@link applyAssetMutationSubmitDefaults} with create semantics. */
export function applyAssetCreateSubmitDefaults(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[],
): Record<string, unknown> {
  return applyAssetMutationSubmitDefaults(body, regions, 'create');
}
